import z from 'zod'

/*** Opt Job Review ***/

const TimestampValueMapSchema = z.record(z.string(), z.any())

export const OptimizationErrorSchema = z.object({
  error_found: z.boolean(),
  error_values: z.record(z.string(), z.array(TimestampValueMapSchema)),
  error_check_name: z.string(),
  error_object_name: z.string(),
  error_object_type: z.string(),
  error_property_names: z.array(z.string()),
  error_check_output_message: z.string(),
})

export const OptJobReviewSchema = z.object({
  input_data: z.any().optional(),
  // Errors are only displayed as JSON, so an entry stored in an older optservice shape must not fail the whole review.
  errors: z.array(z.union([OptimizationErrorSchema, z.string(), z.record(z.string(), z.unknown())])).optional(),
  infeasibility_hint: z.array(z.string()).nullable().optional(),
})

export type OptimizationError = z.infer<typeof OptimizationErrorSchema>
export type OptJobReview = z.infer<typeof OptJobReviewSchema>

/*** Snapshot Diff ***/

export const OptimizationSnapshotResponseSchema = z.object({
  diff: z.object({
    added: z.object({ settings: z.record(z.string(), z.any()).optional() }),
    removed: z.object({ settings: z.record(z.string(), z.any()).optional() }),
    changed: z.object({ settings: z.record(z.string(), z.any()).optional() }),
  }),
  left_snapshot: z.object({
    settings: z.record(z.string(), z.any()),
  }),
  right_snapshot: z.object({
    settings: z.record(z.string(), z.any()),
  }),
})

export type OptimizationSnapshotDiff = z.infer<typeof OptimizationSnapshotResponseSchema>

// *** Troubleshoot Logs **** //

export const OptimizationTroubleshootLogSchema = z.object({
  id: z.number(),
  opt_job: z.number(),
  queue_item: z.number(),
  log_message: z.string(),
  log_data: z.any().nullable().optional(),
  log_level: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
  created_by: z.number(),
  created_by_name: z.string(),
})

export const OptimizationTroubleshootLogsResponseSchema = z.array(OptimizationTroubleshootLogSchema)

export const OptimizationTroubleshootLogCreateSchema = z.object({
  opt_job: z.number(),
  queue_item: z.number().optional(), //queue_item on backend is optional
  log_message: z.string().min(1),
  log_data: z.any().optional(),
  log_level: z.string(),
})

export const OptimizationTroubleshootLogUpdateSchema = z
  .object({
    log_message: z.string().min(1).optional(),
    log_data: z.any().optional().nullable(),
    log_level: z.string().optional(),
  })
  .refine((data) => Object.keys(data).length > 0, {
    message: 'At least one field must be provided for update',
  })

// Delete returns 204 No Content -> represent as void (undefined)
export const OptimizationTroubleshootLogDeleteSchema = z.undefined()

export type OptimizationTroubleshootLog = z.infer<typeof OptimizationTroubleshootLogSchema>
export type OptimizationTroubleshootLogsResponse = z.infer<typeof OptimizationTroubleshootLogsResponseSchema>
export type OptimizationTroubleshootLogCreate = z.infer<typeof OptimizationTroubleshootLogCreateSchema>
export type OptimizationTroubleshootLogUpdate = z.infer<typeof OptimizationTroubleshootLogUpdateSchema>
export type OptimizationTroubleshootLogDelete = z.infer<typeof OptimizationTroubleshootLogDeleteSchema>
